import { apiClient } from './api';
import { qfRestGet } from './qfClient';
import { getTodayDateET } from '../utils/marketDate';
import { underlyingPriceTicker } from '../utils/tickerDisplay';
import type { GexStrikeData, GexApiResponse } from '../types/gex';
import type { OHLCBar } from '../types/tdSequential';

// QF GET /v1/daily-bars response. `date` is a bare ET session date.
interface QfDailyBarsResponse {
  symbol: string;
  start: string;
  end: string;
  bars: {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number | null;
  }[];
}

/** `YYYY-MM-DD` minus `days` calendar days. */
function daysBefore(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export const gexService = {
  /**
   * Fetch daily OHLC from QuantForge, covering any ticker QF has, not just the
   * portal's S&P 500 / Nasdaq-100 set.
   * GET /v1/daily-bars?symbol=&start=&end=
   *
   * `daysBack` is calendar days ending today (ET), as the portal route took it.
   * An uncovered symbol comes back as an empty list, not a 404.
   */
  async fetchOHLC(ticker: string, daysBack: number = 60): Promise<OHLCBar[]> {
    const end = getTodayDateET();
    const response = await qfRestGet<QfDailyBarsResponse>('/v1/daily-bars', {
      // Price keys off the underlying index, never the options root (SPXW -> SPX).
      symbol: underlyingPriceTicker(ticker.toUpperCase()),
      start: daysBefore(end, daysBack),
      end,
    });
    return response.bars.map((b) => ({
      // Consumers read the session date off the front of this string.
      timestamp: `${b.date}T00:00:00Z`,
      open: b.open,
      high: b.high,
      low: b.low,
      close: b.close,
      // QF sends null for cash indices, which have no share volume.
      volume: b.volume ?? 0,
    }));
  },

  /**
   * Fetch Greek Exposures by Strike from portal API
   * GET /api/unusual-whales/greek-exposure/by-strike/{ticker}/
   *
   * Date is auto-determined by the backend if omitted.
   */
  async fetchGexByStrike(ticker: string, date?: string): Promise<GexStrikeData[]> {
    const params = date ? { date } : {};
    const response = await apiClient.get<GexApiResponse>(
      `/api/unusual-whales/greek-exposure/by-strike/${ticker.toUpperCase()}/`,
      params
    );
    return response.data.data;
  },

  /**
   * Fetch Greek Exposures by Strike and Expiry from portal API
   * GET /api/unusual-whales/greek-exposure/by-strike-expiry/{ticker}/?expiry={expiry}&date={date}
   */
  async fetchGexByStrikeExpiry(
    ticker: string,
    date: string | undefined,
    expiry: string
  ): Promise<GexStrikeData[]> {
    const params: Record<string, string> = { expiry };
    if (date) params.date = date;
    const response = await apiClient.get<GexApiResponse>(
      `/api/unusual-whales/greek-exposure/by-strike-expiry/${ticker.toUpperCase()}/`,
      params
    );
    return response.data.data;
  },

  /**
   * Helper to get today's date in YYYY-MM-DD format (US Eastern time)
   */
  getTodayDate(): string {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  },

  /**
   * Helper to get next Friday (common options expiry)
   */
  getNextFriday(): string {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilFriday = dayOfWeek === 5 ? 7 : (5 - dayOfWeek + 7) % 7 || 7;
    const nextFriday = new Date(today);
    nextFriday.setDate(today.getDate() + daysUntilFriday);
    return nextFriday.toISOString().split('T')[0];
  },
};
